import { Component } from '@theme/component';
import { FilterUpdateEvent } from '@theme/events';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';

/**
 * Collection list component.
 * 
 * Original implementation:
 * - On collection templates, intercepts collection card clicks and updates the PLP
 * - via the Section Rendering API instead of a full page navigation.
 *
 * New implementation:
 * - Marks the active collection card on PLP and supports popstate updates.
 * - Cross-collection card navigation uses normal browser navigation so the
 * - destination collection template loads (image clicks match text-link behavior).
 *
 * @extends {Component}
 */
class CollectionListComponent extends Component {
  /** @type {AbortController | null} */
  #navigationAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    /* Disabled: Section Rendering + History API keeps the current template and
       leaves Drinkware visual filters mounted after image clicks.
    this.addEventListener('click', this.#handleClick, { capture: true });
    */
    window.addEventListener('popstate', this.#handlePopState);

    this.#updateActiveCard(new URL(window.location.href).pathname);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    /* this.removeEventListener('click', this.#handleClick, { capture: true }); */
    window.removeEventListener('popstate', this.#handlePopState);
    this.#navigationAbortController?.abort();
  }

  /*
   * On collection templates, intercepts collection card clicks and updates the PLP
   * via the Section Rendering API instead of a full page navigation.
   *
   * @param {MouseEvent} event
   *
  #handleClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (Shopify?.designMode) return;

    const link = event.target instanceof Element ? event.target.closest('a.collection-card__link') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    // Always stop the native navigation for in-component collection cards once we
    // know we can handle them — otherwise the browser follows href before SPA work runs.
    if (!this.#canUseSectionRendering()) return;

    event.preventDefault();
    event.stopPropagation();

    // Ignore further card clicks while a collection update is in progress.
    if (this.getAttribute('aria-busy') === 'true') return;

    const url = this.#getCollectionURL(link);
    if (!url) return;

    const currentURL = new URL(window.location.href);
    if (this.#pathnamesMatch(url.pathname, currentURL.pathname)) return;

    this.#navigate(url, { pushState: true, link });
  };
  */

  #handlePopState = () => {
    // Only one collection-list instance should drive popstate updates.
    if (document.querySelector('collection-list-component') !== this) return;
    if (!this.#canUseSectionRendering()) return;

    const url = new URL(window.location.href);
    this.#navigate(url, { pushState: false });
  };

  /**
   * @param {HTMLAnchorElement} link
   * @returns {URL | null}
   */
  #getCollectionURL(link) {
    // Prefer the browser-resolved href over the raw attribute.
    const href = link.href || link.getAttribute('href');
    if (!href || href === '#') return null;

    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      return url;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} left
   * @param {string} right
   * @returns {boolean}
   */
  #pathnamesMatch(left, right) {
    /** @param {string} pathname */
    const normalize = (pathname) => pathname.replace(/\/$/, '') || '/';
    return normalize(left) === normalize(right);
  }

  /**
   * @returns {boolean}
   */
  #canUseSectionRendering() {
    if (!this.#getPrimarySectionId()) return false;

    const template = document.querySelector('main[data-template]')?.getAttribute('data-template') ?? '';
    if (template.startsWith('collection')) return true;

    // Fallback when data-template is missing/unexpected but we're clearly on a PLP.
    return /\/collections\/[^/]+/.test(window.location.pathname);
  }

  /**
   * @returns {string | null}
   */
  #getPrimarySectionId() {
    const resultsList = document.querySelector('results-list[section-id]');
    const sectionId = resultsList?.getAttribute('section-id');
    return sectionId ? normalizeSectionId(sectionId) : null;
  }

  /**
   * Optional sibling sections (heading, etc.) updated best-effort after the product grid.
   * @param {string} primarySectionId
   * @returns {string[]}
   */
  #getOptionalSectionIds(primarySectionId) {
    const main = document.getElementById('MainContent');
    if (!main) return [];

    const ownSection = this.closest('.shopify-section');
    /** @type {string[]} */
    const sectionIds = [];

    for (const section of main.querySelectorAll('.shopify-section')) {
      if (!(section instanceof HTMLElement)) continue;
      if (section === ownSection) continue;
      if (!section.id) continue;

      const sectionId = normalizeSectionId(section.id);
      if (sectionId === primarySectionId) continue;

      sectionIds.push(sectionId);
    }

    return sectionIds;
  }

  /**
   * @param {URL} url
   * @param {{ pushState?: boolean, link?: HTMLAnchorElement | null }} [options]
   */
  async #navigate(url, { pushState = true, link = null } = {}) {
    this.#navigationAbortController?.abort();
    this.#navigationAbortController = new AbortController();
    const { signal } = this.#navigationAbortController;

    this.#setLoadingState(url.pathname);

    // Ensure the loading state paints before we start awaiting network work.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (signal.aborted) return;

    try {
      if (pushState) {
        history.pushState({}, '', url.toString());
      }

      this.dispatchEvent(new FilterUpdateEvent(url.searchParams));

      await startViewTransition(async () => {
        if (signal.aborted) return;
        await this.#renderSections(url, signal);
      }, ['product-grid']);

      if (signal.aborted) return;

      const titleLink = link ?? this.querySelector('a.collection-card__link[aria-current="page"]');
      this.#updateDocumentTitle(titleLink instanceof HTMLAnchorElement ? titleLink : null);
    } catch (error) {
      if (signal.aborted) return;

      console.error('Failed to update collection via Section Rendering API:', error);
      window.location.assign(url.toString());
    } finally {
      if (this.#navigationAbortController?.signal === signal) {
        this.#clearLoadingState();
      }
    }
  }

  /**
   * @param {URL} url
   * @param {AbortSignal} signal
   */
  async #renderSections(url, signal) {
    const primarySectionId = this.#getPrimarySectionId();
    if (!primarySectionId) {
      throw new Error('No results list section found');
    }

    // Clone the URL per request — sectionRenderer mutates searchParams on the URL object.
    await sectionRenderer.renderSection(primarySectionId, {
      url: new URL(url),
      cache: false,
    });

    if (signal.aborted) return;

    const optionalSectionIds = this.#getOptionalSectionIds(primarySectionId);
    if (!optionalSectionIds.length) return;

    // Best-effort updates for heading/other PLP sections — don't fail the navigation if one errors.
    await Promise.allSettled(
      optionalSectionIds.map((sectionId) =>
        sectionRenderer.renderSection(sectionId, {
          url: new URL(url),
          cache: false,
        })
      )
    );
  }

  /**
   * Marks the target card as loading and disables the rest so the wait is obvious.
   * @param {string} pathname
   */
  #setLoadingState(pathname) {
    this.setAttribute('aria-busy', 'true');
    this.#updateActiveCard(pathname);

    for (const card of this.querySelectorAll('.collection-card')) {
      if (!(card instanceof HTMLElement)) continue;

      const link = card.querySelector('a.collection-card__link');
      const linkURL = link instanceof HTMLAnchorElement ? this.#getCollectionURL(link) : null;
      const isTarget = Boolean(linkURL && this.#pathnamesMatch(linkURL.pathname, pathname));

      if (isTarget) {
        card.classList.add('collection-card--loading');
        card.removeAttribute('inert');
      } else {
        card.classList.remove('collection-card--loading');
        card.setAttribute('inert', '');
      }
    }
  }

  #clearLoadingState() {
    this.removeAttribute('aria-busy');

    for (const card of this.querySelectorAll('.collection-card')) {
      if (!(card instanceof HTMLElement)) continue;

      card.classList.remove('collection-card--loading');
      card.removeAttribute('inert');
    }
  }

  /**
   * @param {string} pathname
   */
  #updateActiveCard(pathname) {
    const links = this.querySelectorAll('a.collection-card__link');

    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const linkURL = this.#getCollectionURL(link);
      const isCurrent = Boolean(linkURL && this.#pathnamesMatch(linkURL.pathname, pathname));

      if (isCurrent) {
        link.setAttribute('aria-current', 'page');
        link.closest('.collection-card')?.classList.add('collection-card--active');
      } else {
        link.removeAttribute('aria-current');
        link.closest('.collection-card')?.classList.remove('collection-card--active');
      }
    }
  }

  /**
   * @param {HTMLAnchorElement | null} link
   */
  #updateDocumentTitle(link) {
    const collectionTitle = link?.querySelector('.visually-hidden')?.textContent?.trim();
    if (!collectionTitle) return;

    const separatorMatch = document.title.match(/\s+[\u2013\-|]\s+/);
    if (separatorMatch) {
      const shopName = document.title.slice(
        (separatorMatch.index ?? 0) + separatorMatch[0].length
      );
      document.title = shopName ? `${collectionTitle} – ${shopName}` : collectionTitle;
      return;
    }

    document.title = collectionTitle;
  }
}

if (!customElements.get('collection-list-component')) {
  customElements.define('collection-list-component', CollectionListComponent);
}
